/**
 * Disturbed-water ribbon behind the boat. Phaser-free so the fade and width can be unit-tested
 * without drawing; the scene only fills the quads.
 */

import { BOAT_LENGTH_M, TRAIL_LENGTH_DEFAULT_M, metersToPixels } from './constants.ts';
import { add, clamp, length, normalize, scale, sub } from './math.ts';
import { clipPolylineToProgress, sampleMovePath, type MovePath } from './path.ts';
import type { Vec2 } from './types.ts';

export type WakeQuad = {
  a: Vec2;
  b: Vec2;
  c: Vec2;
  d: Vec2;
  alpha: number;
};

export type Wake = {
  wash: WakeQuad[];
  foam: WakeQuad[];
  arms: WakeQuad[];
};

const EMPTY_WAKE: Wake = { wash: [], foam: [], arms: [] };
const BEAM_PX = metersToPixels(BOAT_LENGTH_M / 2);
/** Fraction of hull length from centre back to the painted transom. */
export const STERN_OFFSET_LENGTH_FACTOR = 0.42;
/** Recorded paths follow the hull centre; the ribbon is the transom's track. */
const STERN_OFFSET_PX = metersToPixels(BOAT_LENGTH_M * STERN_OFFSET_LENGTH_FACTOR);
const DEFAULT_WAKE_LENGTH_PX = metersToPixels(TRAIL_LENGTH_DEFAULT_M);
const EPSILON = 1e-6;

/**
 * How wide a collision ripple spreads. A harder closing speed throws a wider
 * ring; the bounds keep a tap visible and a ram from covering the bay.
 */
export function collisionSplashRadius(closingPx: number): number {
  return clamp(
    metersToPixels(1.5) + Math.max(0, closingPx) * 0.08,
    metersToPixels(2),
    metersToPixels(9),
  );
}

export function buildWake(
  paths: readonly MovePath[],
  latestProgress = 1,
  maxLengthPx = DEFAULT_WAKE_LENGTH_PX,
): Wake {
  const points = trimToLength(
    shiftToStern(flattenTrail(paths, latestProgress)),
    maxLengthPx,
  );

  if (points.length < 2) {
    return EMPTY_WAKE;
  }

  const normals = pathNormals(points);
  const last = points.length - 1;
  const wash: WakeQuad[] = [];
  const foam: WakeQuad[] = [];
  const arms: WakeQuad[] = [];

  for (let i = 0; i < last; i += 1) {
    const age0 = i / last;
    const age1 = (i + 1) / last;
    const alpha = (age0 ** 2.4 + age1 ** 2.4) / 2;

    if (alpha < 0.02) {
      continue;
    }

    const halfWash0 = halfWidth(age0, 0.55, 1.55);
    const halfWash1 = halfWidth(age1, 0.55, 1.55);
    const halfFoam0 = halfWash0 * 0.38;
    const halfFoam1 = halfWash1 * 0.38;
    const halfArm0 = halfWash0 * 1.12;
    const halfArm1 = halfWash1 * 1.12;
    const band0 = halfWash0 * 0.2;
    const band1 = halfWash1 * 0.2;

    const body = ribbon(points, normals, i, halfWash0, halfWash1, alpha * 0.42);
    const core = ribbon(points, normals, i, halfFoam0, halfFoam1, alpha * 0.75);
    const left = ribbonBand(points, normals, i, halfArm0, halfArm1, band0, band1, 1, alpha * 0.36);
    const right = ribbonBand(points, normals, i, halfArm0, halfArm1, band0, band1, -1, alpha * 0.36);

    if (body) {
      wash.push(body);
    }

    if (core) {
      foam.push(core);
    }

    if (left) {
      arms.push(left);
    }

    if (right) {
      arms.push(right);
    }
  }

  return { wash, foam, arms };
}

function flattenTrail(paths: readonly MovePath[], latestProgress: number): Vec2[] {
  const points: Vec2[] = [];
  const lastIndex = paths.length - 1;

  for (let i = 0; i < paths.length; i += 1) {
    let sampled = sampleMovePath(paths[i]!);

    if (i === lastIndex) {
      sampled = clipPolylineToProgress(sampled, latestProgress);
    }

    if (sampled.length === 0) {
      continue;
    }

    const last = points.at(-1);
    const first = sampled[0]!;

    if (last && last.x === first.x && last.y === first.y) {
      points.push(...sampled.slice(1));
    } else {
      points.push(...sampled);
    }
  }

  return points;
}

function trimToLength(points: readonly Vec2[], maxLength: number): Vec2[] {
  if (points.length < 2 || maxLength <= 0) {
    return [...points];
  }

  const newest = points[points.length - 1]!;
  const trimmed: Vec2[] = [{ ...newest }];
  let remaining = maxLength;

  for (let i = points.length - 1; i > 0; i -= 1) {
    const newer = points[i]!;
    const older = points[i - 1]!;
    const span = length(sub(newer, older));

    if (span < EPSILON) {
      continue;
    }

    if (span <= remaining) {
      trimmed.push({ ...older });
      remaining -= span;
      continue;
    }

    trimmed.push(add(newer, scale(normalize(sub(older, newer)), remaining)));
    break;
  }

  trimmed.reverse();
  return trimmed;
}

function shiftToStern(points: readonly Vec2[]): Vec2[] {
  if (points.length < 2) {
    return [...points];
  }

  const stern: Vec2[] = [];

  for (let i = 0; i < points.length; i += 1) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const tangent = sub(next, prev);

    if (length(tangent) < EPSILON) {
      stern.push(stern[i - 1] ?? { ...points[i]! });
      continue;
    }

    stern.push(sub(points[i]!, scale(normalize(tangent), STERN_OFFSET_PX)));
  }

  return stern;
}

function halfWidth(age: number, newest: number, oldest: number): number {
  return (BEAM_PX / 2) * (oldest + (newest - oldest) * age);
}

function pathNormals(points: readonly Vec2[]): Vec2[] {
  const normals: Vec2[] = [];

  for (let i = 0; i < points.length; i += 1) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const tangent = sub(next, prev);

    if (length(tangent) < EPSILON) {
      normals.push(normals[i - 1] ?? { x: 0, y: 1 });
      continue;
    }

    const unit = normalize(tangent);
    normals.push({ x: -unit.y, y: unit.x });
  }

  return normals;
}

function offset(point: Vec2, normal: Vec2, distance: number): Vec2 {
  return add(point, scale(normal, distance));
}

function ribbon(
  points: readonly Vec2[],
  normals: readonly Vec2[],
  index: number,
  half0: number,
  half1: number,
  alpha: number,
): WakeQuad | null {
  return makeQuad(
    offset(points[index]!, normals[index]!, half0),
    offset(points[index]!, normals[index]!, -half0),
    offset(points[index + 1]!, normals[index + 1]!, -half1),
    offset(points[index + 1]!, normals[index + 1]!, half1),
    alpha,
  );
}

function ribbonBand(
  points: readonly Vec2[],
  normals: readonly Vec2[],
  index: number,
  centre0: number,
  centre1: number,
  band0: number,
  band1: number,
  side: 1 | -1,
  alpha: number,
): WakeQuad | null {
  const inner0 = side * (centre0 - band0);
  const outer0 = side * (centre0 + band0);
  const inner1 = side * (centre1 - band1);
  const outer1 = side * (centre1 + band1);

  return makeQuad(
    offset(points[index]!, normals[index]!, outer0),
    offset(points[index]!, normals[index]!, inner0),
    offset(points[index + 1]!, normals[index + 1]!, inner1),
    offset(points[index + 1]!, normals[index + 1]!, outer1),
    alpha,
  );
}

function makeQuad(a: Vec2, b: Vec2, c: Vec2, d: Vec2, alpha: number): WakeQuad | null {
  if (![a, b, c, d].every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) {
    return null;
  }

  if (!Number.isFinite(alpha) || alpha <= 0) {
    return null;
  }

  return { a, b, c, d, alpha };
}
